import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { serviceOptions, submitLeadForm } from '../mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

export const LeadForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitLeadForm(formData);
      if (result.success) {
        setIsSubmitted(true);
        toast.success(result.message || 'Vielen Dank! Wir melden uns in Kürze bei Ihnen.');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({ name: '', email: '', phone: '', service: '', message: '' });
          setIsSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(
        error.message || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an: 01515 9182828'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Jetzt <span className="text-blue-400">Zulassung starten</span>
          </h2>
          <p className="text-lg text-gray-300">
            Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-2 border-gray-700 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <CardTitle className="text-2xl">Anfrage senden</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Anfrage erfolgreich gesendet!
                </h3>
                <p className="text-gray-600">
                  Wir melden uns in Kürze bei Ihnen.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Ihr vollständiger Name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="h-12 text-base"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                    E-Mail-Adresse *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ihre.email@beispiel.de"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="h-12 text-base"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                    Telefonnummer *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Ihre Telefonnummer"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="h-12 text-base"
                    required
                  />
                </div>

                {/* Service Selection */}
                <div>
                  <Label htmlFor="service" className="text-base font-semibold mb-2 block">
                    Anliegen *
                  </Label>
                  <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Bitte wählen Sie Ihr Anliegen" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-base font-semibold mb-2 block">
                    Nachricht (optional)
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Weitere Details zu Ihrem Anliegen..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="min-h-[120px] text-base"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="mr-2">Wird gesendet...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Jetzt Zulassung starten
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center mt-4">
                  * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
                </p>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-gray-300 mb-4">
            Oder kontaktieren Sie uns direkt:
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="tel:01515 9182828"
              className="flex items-center space-x-2 text-white hover:text-blue-400 transition-colors font-semibold"
            >
              <span>📞 01515 9182828</span>
            </a>
            <span className="hidden sm:block text-gray-500">|</span>
            <a 
              href={`https://wa.me/4915159182828`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-white hover:text-blue-400 transition-colors font-semibold"
            >
              <span>💬 WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
